/**
 * How an offer's deadline should be presented, given how long is left.
 *
 * The banner used to read "OFFER EXPIRES 10 DAYS AFTER PICTURE DAY". Nobody
 * does that arithmetic: it asks a parent to recall a date and add to it. The
 * server now sends `expiresOn` — computed from that student's OWN sitting, so
 * two children photographed on different days get different deadlines from the
 * same coupon — and this turns it into a date and an urgency.
 *
 * ONE implementation. The phase decides tone AND copy together; splitting them
 * is how a red banner ends up saying "plenty of time".
 */

export const PHASES = {
  VALUE: 'value',
  REMINDER: 'reminder',
  URGENT: 'urgent',
  FINAL: 'final',
};

// The offer runs to the END of its last day, not to midnight at its start.
// Getting this wrong shortens every offer by a day, so the banner counts down
// one day short and turns red while a full day still remains.
export function expiryMoment(expiresOn) {
  if (!expiresOn) return null;
  // Parse as local midnight rather than letting Date() read "2026-08-21" as
  // UTC, which lands the previous evening for every US timezone.
  const [y, m, d] = String(expiresOn).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 23, 59, 59, 999);
}

export function msRemaining(expiresOn, now = new Date()) {
  const end = expiryMoment(expiresOn);
  return end ? end.getTime() - now.getTime() : null;
}

/**
 * Whole days left, counting the current day as one. 23 hours left is "1 day",
 * not "0" — a banner saying "0 DAYS LEFT" beside a working coupon reads broken.
 */
export function daysRemaining(expiresOn, now = new Date()) {
  const ms = msRemaining(expiresOn, now);
  return ms === null ? null : Math.ceil(ms / 86400000);
}

export function phaseFor(expiresOn, now = new Date()) {
  const ms = msRemaining(expiresOn, now);
  if (ms === null) return null;
  if (ms <= 0) return null; // expired: the offer should not be resolved at all
  if (ms < 86400000) return PHASES.FINAL;

  const days = daysRemaining(expiresOn, now);
  if (days <= 4) return PHASES.URGENT;
  if (days <= 7) return PHASES.REMINDER;
  return PHASES.VALUE;
}

// "Friday, October 24" — the weekday matters. A parent plans around "Friday"
// far more readily than around "the 24th".
export function formatDeadline(expiresOn) {
  const end = expiryMoment(expiresOn);
  if (!end) return null;
  return end.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

// "6:42:15" for the final-day countdown.
export function formatCountdown(ms) {
  if (ms === null || ms <= 0) return null;
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${h}:${pad(m)}:${pad(s)}`;
}

/**
 * Tone per phase. Amber and red are earned, not decorative: if the banner is
 * loud on day 12 it says nothing on day 1.
 */
export const PHASE_STYLE = {
  [PHASES.VALUE]: { background: 'linear-gradient(90deg, #0C447C, #14608F)' },
  [PHASES.REMINDER]: { background: 'linear-gradient(90deg, #0C447C, #14608F)' },
  [PHASES.URGENT]: { background: 'linear-gradient(90deg, #9A5B00, #C27803)' },
  [PHASES.FINAL]: { background: 'linear-gradient(90deg, #9B1C1C, #C2410C)' },
};
